import {
    Box,
    Checkbox,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { HeadCellDto } from '../../services/dtos/head-cell.dto';
import { StyledTableRow } from './table-row';
import React, { ReactElement, useEffect, useState } from 'react';
import { visuallyHidden } from '@mui/utils';

//On type l'ordre
type Order = 'asc' | 'desc';
/**
 * Composant du tableau des pages
 * @param props Pages à afficher
 * @constructor
 */
export default function StyledTable<T extends object, K extends keyof T>(
    props: Readonly<{
        //données T = object que l'on passe au tableau
        data: Array<T>;
        searching: boolean;
        //Headers du tableau K veux dire que l'id de l'objet HeadCellDto correspond a un attribut de T
        headCells: Array<HeadCellDto<T, K>>;
        //Colonne action
        action?: (currentValue: T) => ReactElement;
        page?: number;
        itemsPerPage?: number;
        totalItems?: number;
        customCase?: (currentValue: T) => ReactElement;
        handleChangePage?: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
        handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
        //Permet de checker ou non une seule ligne
        check?: (currentValue: T) => void;
        //Permet de checker ou non toute les lignes
        checkAll?: (checked: boolean) => void;
        //Libelle qui correspond au fait de checker une ligne
        labelCheck?: string;
        //Action sur les lignes checks
        checkAction?: () => ReactElement;
        //handler sort by
        handlerSortBy?: (order: Order, orderBy: keyof T) => void;
        //order
        order?: Order;
        //sort
        sort?: keyof T;
        minWidth?: number | string | { xs?: number | string; sm?: number | string; md?: number | string; lg?: number | string };
    }>
) {
    //On ajout par defaut un attribut custom checked
    //Cet attribut va permettre de checker ou non  les lignes
    const [rows, setRows] = useState<Array<T & { checked: boolean; idLineTable: number }>>();

    //useEffect pour les lignes
    useEffect(() => {
        setRows(
            props.data.map((obj, index) => ({
                ...obj,
                checked: false,
                idLineTable: index,
            }))
        );
    }, [props.data]);

    //GEstion du checkall
    const checkedAll = (checked: boolean) => {
        setRows((rows ?? []).map(obj => ({ ...obj, checked })));
    };

    //Onchange sur les checkbox a l'unité
    const onChange = (checked: boolean, row: T) => {
        // on set seulement la ligne que l'on veut
        setRows((rows ?? []).map(value => ({ ...value, checked: value === row ? checked : value.checked })));
        props.check?.(row);
    };

    //Onchange sur la checkbox qui change les valeurs de toutes les autres
    const onChangeAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        checkedAll(checked);
        props.checkAll?.(checked);
    };

    //Handler pour le sort
    const createSortHandler = (property: keyof T) => (_event: React.MouseEvent<unknown>) => {
        //On met asc par defaut
        let newOrder: Order = 'asc';
        //Si order egale asc et qu'on est tjr sur la même colonne alors on change.
        if (props.order === 'asc' && props.sort !== undefined && props.sort === property) {
            newOrder = 'desc';
        }
        props.handlerSortBy?.(newOrder, property);
    };

    // @ts-ignore
    return (
        // Container du tableau
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                border: '1px solid #e2e7f2',
                borderRadius: '18px',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                padding: 0,
                backgroundColor: '#ffffff',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                    height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#d5deeb',
                    borderRadius: 999,
                },
            }}
        >
            <Table
                sx={{
                    minWidth: props.minWidth ?? { xs: 560, sm: 640 },
                    tableLayout: 'auto',
                }}
            >
                {/*// Affichage du tableau*/}
                <TableHead
                    sx={{
                        '& .MuiTableRow-root': {
                            backgroundColor: '#f6f9fc',
                        },
                    }}
                >
                    <TableRow>
                        {/*En-têtes*/}
                        {props.headCells.map(headCell => (
                            <TableCell
                                key={`headCell-${headCell.id.toString()}`}
                                align={'left'}
                                sx={{
                                    borderBottom: '1px solid #dfe7f1',
                                    color: '#345072',
                                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    padding: { xs: '12px 10px', sm: '14px 16px' },
                                    whiteSpace: 'nowrap',
                                    width: headCell.width ? headCell.width + '%' : 100,
                                }}
                                sortDirection={props.sort === headCell.id ? props.order : false}
                            >
                                {headCell.sort ? (
                                    <TableSortLabel
                                        active={props.sort === headCell.id}
                                        direction={props.sort === headCell.id ? props.order : 'asc'}
                                        onClick={createSortHandler(headCell.id)}
                                    >
                                        {headCell.label}
                                        {props.sort === headCell.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {props.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                ) : (
                                    headCell.label
                                )}
                            </TableCell>
                        ))}
                        {props.action && (
                            <TableCell
                                key={`headCell-action`}
                                align={'left'}
                                sx={{
                                    borderBottom: '1px solid #dfe7f1',
                                    color: '#345072',
                                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    padding: { xs: '12px 10px', sm: '14px 16px' },
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Action
                            </TableCell>
                        )}

                        {props.check && (
                            <TableCell
                                key={`headCell-sendMail`}
                                align={'left'}
                                sx={{
                                    borderBottom: '1px solid #dfe7f1',
                                    color: '#345072',
                                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    padding: { xs: '12px 10px', sm: '14px 16px' },
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {props.labelCheck}
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!props.searching ? (
                        <>
                            {/*// Lignes du tableau*/}
                            {rows?.map(row => (
                                // Ajout d'une row stylisée
                                <StyledTableRow key={`row-${row.idLineTable}`}>
                                    {/*En-têtes*/}
                                    {props.headCells.map(headCell => (
                                        // La key id du headcell doit permettre d'avoir l'information de la row
                                        <TableCell
                                            key={`rowCell-${headCell.id.toString()}`}
                                            sx={{
                                                color: '#334155',
                                                fontSize: { xs: '0.86rem', sm: '0.95rem' },
                                                padding: { xs: '12px 10px', sm: '14px 16px' },
                                                verticalAlign: 'middle',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            <>
                                                {headCell.custom && props.customCase
                                                    ? props.customCase(row)
                                                    : row[headCell.id]}
                                            </>
                                        </TableCell>
                                    ))}
                                    {props.action && (
                                        // S'il y a une action, on ajout le react element personnalisé
                                        <TableCell
                                            key={`rowCell-${row.idLineTable}-action`}
                                            sx={{
                                                padding: { xs: '12px 10px', sm: '14px 16px' },
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {props.action(row)}
                                        </TableCell>
                                    )}
                                    {props.check && (
                                        // S'il y a une action, on ajout le react element personnalisé
                                        <TableCell
                                            key={`rowCell-${row.idLineTable}-sendMail`}
                                            sx={{
                                                padding: { xs: '12px 10px', sm: '14px 16px' },
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                data-testid={`checkbox-${row.idLineTable}`}
                                                checked={row.checked}
                                                onChange={(
                                                    _event: React.ChangeEvent<HTMLInputElement>,
                                                    checked: boolean
                                                ) => onChange(checked, row)}
                                            />
                                        </TableCell>
                                    )}
                                </StyledTableRow>
                            ))}
                            {props.checkAll && (
                                // S'il y a une action, on ajout le react element personnalisé
                                <StyledTableRow>
                                    <TableCell colSpan={props.headCells?.length - 4 + 1 + (props.action ? 1 : 0)} />
                                    <TableCell
                                        colSpan={3}
                                        sx={{
                                            padding: { xs: '12px 10px', sm: '14px 16px' },
                                            fontWeight: 'bold',
                                            textAlign: 'right',
                                        }}
                                    >
                                        Sélectionner tous les membres
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: { xs: '12px 10px', sm: '14px 16px' },
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Checkbox data-testid={`checkboxAll`} onChange={onChangeAll} />
                                    </TableCell>
                                </StyledTableRow>
                            )}
                            {props.checkAction && (
                                <StyledTableRow>
                                    <TableCell colSpan={props.headCells?.length + (props.action ? 1 : 0)} />
                                    <TableCell sx={{ padding: { xs: '12px 10px', sm: '14px 16px' } }}>
                                        {props.checkAction()}
                                    </TableCell>
                                </StyledTableRow>
                            )}
                        </>
                    ) : (
                        //Affichage d'une barre de progression lors de la récupération des données
                        <TableRow>
                            <TableCell colSpan={props.headCells?.length}>
                                <LinearProgress />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                {props.page !== undefined &&
                props.itemsPerPage !== undefined &&
                props.handleChangePage !== undefined ? (
                    <TableFooter>
                        <TableRow>
                            {/*Options de pagination*/}
                            <TablePagination
                                sx={{
                                    borderTop: '1px solid #e7edf5',
                                    '& .MuiTablePagination-toolbar': {
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        minHeight: { xs: 88, sm: 62 },
                                        paddingLeft: { xs: 1.5, sm: 2 },
                                        paddingRight: { xs: 1.5, sm: 2 },
                                        paddingTop: { xs: 1, sm: 0 },
                                        paddingBottom: { xs: 1, sm: 0 },
                                    },
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontSize: { xs: '0.78rem', sm: '0.875rem' },
                                        margin: 0,
                                    },
                                    '& .MuiTablePagination-actions': {
                                        marginLeft: { xs: 'auto', sm: 2 },
                                    },
                                    '& .MuiInputBase-root': {
                                        fontSize: { xs: '0.82rem', sm: '0.875rem' },
                                    },
                                }}
                                count={Number(props.totalItems)}
                                page={props.page}
                                onPageChange={props.handleChangePage}
                                rowsPerPage={props.itemsPerPage}
                                onRowsPerPageChange={props.handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                showFirstButton
                                showLastButton
                            />
                        </TableRow>
                    </TableFooter>
                ) : (
                    <></>
                )}
            </Table>
        </TableContainer>
    );
}
